
import { supabase } from "@/integrations/supabase/client";

// Define a type for the team member data
export type TeamMember = {
  name: string;
  position: string;
  bio: string;
  image: string;
};

// Team members data with Supabase image paths
export const teamMembers: TeamMember[] = [
  {
    name: "Yosef Adelman",
    position: "CEO",
    bio: "Leading our company with vision and expertise, Yosef ensures we deliver exceptional results for all our clients.",
    image: "yosef-adelman.jpg",
  },
  {
    name: "Olivia Cancelli",
    position: "Director of Operations",
    bio: "Olivia oversees our day-to-day operations, ensuring all projects are executed with precision and efficiency.",
    image: "olivia-cancelli.jpg",
  },
  {
    name: "Shirel Swissa",
    position: "Social Media Manager",
    bio: "Shirel crafts engaging social media strategies that build brand presence and generate meaningful engagement.",
    image: "shirel-swissa.jpg",
  },
  {
    name: "Shelby Nicholas",
    position: "Project Manager",
    bio: "Shelby ensures timely delivery of all client projects while maintaining our high standards of quality.",
    image: "shelby-nicholas.jpg",
  },
  {
    name: "Athar Rasool",
    position: "CTO",
    bio: "Athar leads our technical team, designing innovative solutions that drive measurable results for our clients.",
    image: "athar-rasool.jpg",
  },
  {
    name: "Drew Goulson",
    position: "Admin",
    bio: "Drew keeps our operations running smoothly by managing administrative processes with expert attention to detail.",
    image: "drew-goulson.jpg",
  },
];

// Function to get the public URL for a team member image
export const getTeamMemberImageUrl = (imageName: string): string => {
  try {
    const { data } = supabase.storage.from('team_images').getPublicUrl(imageName);
    return data.publicUrl;
  } catch (error) {
    console.error("Error getting image URL:", error);
    return "";
  }
};

// Helper function to display a fallback image if the main image fails to load
export const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  const target = e.target as HTMLImageElement;
  target.onerror = null;
  target.src = "https://images.unsplash.com/photo-1506748686214-e9df14d4d9d0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=320&h=320&q=80";
};

// Static URLs for team member images to use during static build
export const staticTeamImageUrls: Record<string, string> = {
  "yosef-adelman.jpg": "https://losangelesseogroup.com/team-images/yosef-adelman.jpg",
  "olivia-cancelli.jpg": "https://losangelesseogroup.com/team-images/olivia-cancelli.jpg",
  "shirel-swissa.jpg": "https://losangelesseogroup.com/team-images/shirel-swissa.jpg",
  "shelby-nicholas.jpg": "https://losangelesseogroup.com/team-images/shelby-nicholas.jpg",
  "athar-rasool.jpg": "https://losangelesseogroup.com/team-images/athar-rasool.jpg",
  "drew-goulson.jpg": "https://losangelesseogroup.com/team-images/drew-goulson.jpg",
};

// Function to trigger the image upload edge function
export const uploadTeamImages = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase.functions.invoke('upload-team-images');
    
    if (error) {
      console.error('Error uploading team images:', error);
      return false;
    }
    
    console.log('Team images uploaded successfully:', data);
    return true;
  } catch (err) {
    console.error('Failed to invoke upload-team-images function:', err);
    return false;
  }
};

// Function to prefetch all team member images
export const prefetchTeamImages = (): Record<string, string> => {
  const imageUrls: Record<string, string> = {};
  
  try {
    // For static builds or SSG, use the static URLs
    if (typeof window === 'undefined' || import.meta.env.PROD) {
      return { ...staticTeamImageUrls };
    }
    
    // For client-side rendering, get URLs from Supabase
    teamMembers.forEach(member => {
      imageUrls[member.image] = getTeamMemberImageUrl(member.image);
    });
  } catch (error) {
    console.error('Error prefetching team images:', error);
    // Fall back to static URLs if there's an error
    return { ...staticTeamImageUrls };
  }
  
  return imageUrls;
};
